import { z } from 'zod/mini';
import type { components } from '@/api/generated-types';
import { DonorError } from '@/api/errors';
const text = z.string();
const id = z.uuid();
const date = text.check(z.refine((value) => Number.isFinite(Date.parse(value))));
const amount = z.int().check(z.gte(0)).check(z.lte(Number.MAX_SAFE_INTEGER));
export const currencySchema = z.enum(['RON', 'EUR', 'USD']);
export const tokenSchema = z.object({
  accessToken: text.check(z.minLength(1)),
  accessTokenExpiresAt: date,
});
export const messageSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('logout'), fence: text.check(z.minLength(1)) }),
  z.object({
    kind: z.literal('tokens'),
    fence: text.check(z.minLength(1)),
    userId: text.check(z.minLength(1)),
    pair: tokenSchema,
  }),
]);
export const profileSchema = z.looseObject({
  user: z.looseObject({
    id,
    email: z.email(),
    emailVerifiedAt: z.nullable(date),
    firstName: z.optional(z.nullable(text)),
    lastName: z.optional(z.nullable(text)),
    twoFactorEnabled: z.optional(z.boolean()),
    mustEnableTwoFactor: z.optional(z.boolean()),
  }),
  preferences: z.looseObject({
    locale: z.optional(text),
    notifReceiptsEmail: z.optional(z.boolean()),
    selectedChurchId: z.optional(z.nullable(id)),
  }),
  donor: z.nullable(
    z.looseObject({
      donationCount: z.optional(amount),
      projectsSupportedCount: z.optional(amount),
    }),
  ),
});
const adjustment = z.enum([
  'NONE',
  'REFUND_PENDING',
  'REFUNDED',
  'PARTIALLY_REFUNDED',
  'DISPUTED',
  'DISPUTE_LOST',
]);
const line = z.looseObject({
  projectId: id,
  projectTitle: text,
  organizationId: id,
  organizationName: text,
  collectingOrganizationName: text,
  amount,
});
const quoteShape = z.looseObject({
  id,
  currency: currencySchema,
  amount,
  expiresAt: date,
  policyVersion: text.check(z.minLength(1)),
  originChurchId: z.nullable(id),
  originChurchName: z.nullable(text),
  items: z.array(
    z.extend(line, {
      publicOrganization: z.looseObject({
        id,
        name: text,
        slug: text,
        type: z.enum(['UNION', 'CONFERENCE', 'CHURCH']),
      }),
      organizationType: z.enum(['UNION', 'CONFERENCE', 'CHURCH']),
      collectingOrganizationId: id,
      currency: currencySchema,
      fundType: z.enum(['TITHE', 'OFFERING', 'GENERAL']),
      categoryId: z.nullable(id),
      categoryName: z.nullable(text),
      note: z.optional(text),
    }),
  ),
});
const quote = z.pipe(
  quoteShape,
  z.transform((value) => ({
    ...value,
    items: value.items.map(({ note, ...item }) => (note === undefined ? item : { ...item, note })),
  })),
) satisfies z.ZodMiniType<components['schemas']['BasketQuoteResponseDto']>;
export const checkoutSchema = z.looseObject({
  id,
  channel: z.enum(['MOBILE_APP', 'PUBLIC_WEB', 'MANUAL', 'UNKNOWN']),
  currency: currencySchema,
  amount,
  confirmedAt: z.nullable(date),
  cancelledAt: z.nullable(date),
  originChurchId: z.nullable(id),
  originChurchName: z.nullable(text),
  simulation: z.boolean(),
  capabilityExpiresAt: z.nullable(date),
  status: z.enum(['OPEN', 'PROCESSING', 'COMPLETED', 'CANCELLED', 'REVIEW_REQUIRED']),
  paymentStatus: z.enum(['PENDING', 'PROCESSING', 'SUCCEEDED', 'CANCELLED']),
  cancellationPending: z.boolean(),
  createdAt: date,
  adjustmentStatus: adjustment,
  refundedAmount: amount,
  items: z.array(
    z.extend(line, {
      id,
      refundedAmount: amount,
      adjustmentStatus: adjustment,
      fundType: z.enum(['TITHE', 'OFFERING', 'GENERAL']),
      categoryId: z.nullable(id),
      note: z.nullable(text),
      status: z.enum([
        'PENDING',
        'SUCCEEDED',
        'FAILED',
        'PENDING_REFUND',
        'REFUNDED',
        'PARTIALLY_REFUNDED',
        'CANCELLED',
        'DISPUTED',
        'DISPUTE_LOST',
      ]),
    }),
  ),
});
export const documentsSchema = z.looseObject({
  items: z.array(
    z.looseObject({
      id,
      kind: z.enum(['RECEIPT', 'CREDIT_NOTE', 'CREDIT_REVERSAL']),
      number: text,
      currency: currencySchema,
      amount,
      beneficiary: text,
      issuer: text,
      issuedAt: date,
      available: z.boolean(),
    }),
  ),
});
export const giftSchema = z.object({
  projectId: id,
  organizationId: id,
  currency: currencySchema,
  amount: amount.check(z.gt(0)),
});
export const draftSchema = z.object({
  revision: text.check(z.minLength(1)),
  savedAt: amount,
  items: z.array(giftSchema).check(z.maxLength(150)),
});
export const attemptSchema = z
  .object({
    actorRef: text.check(z.minLength(1)),
    mode: z.enum(['guest', 'account']),
    key: text.check(z.minLength(1)),
    currency: currencySchema,
    draftKey: text.check(z.minLength(1)),
    revision: text.check(z.minLength(1)),
    quoteId: id,
    amount: z.optional(amount.check(z.gt(0))),
    phase: z.enum(['CREATING', 'CREATED']),
    checkoutId: z.optional(id),
    createdAt: amount,
  })
  .check(z.refine((value) => value.phase !== 'CREATED' || value.checkoutId !== undefined));
export function parseCriticalResponse(path: string, value: unknown): unknown {
  const pathname = path.split('?')[0] ?? path;
  let schema: z.ZodMiniType | undefined;
  if (pathname === '/me/profile') schema = profileSchema;
  else if (pathname === '/me/preferences')
    schema = z.looseObject({ locale: z.enum(['ro', 'en', 'hu']) });
  else if (pathname === '/auth/web/refresh') schema = tokenSchema;
  else if (['/auth/web/2fa/verify', '/auth/web/magic-link/consume'].includes(pathname))
    schema = z.union([tokenSchema, z.looseObject({ requires2FA: z.literal(true) })]);
  else if (pathname === '/auth/2fa/setup')
    schema = z.looseObject({
      secret: z.optional(text.check(z.minLength(1))),
      otpauthUri: z.optional(text.check(z.startsWith('otpauth://'))),
      recoveryCodes: z.array(text.check(z.minLength(1))).check(z.minLength(1)),
    });
  else if (pathname === '/auth/2fa/recovery-codes/regenerate')
    schema = z.looseObject({
      recoveryCodes: z.array(text.check(z.minLength(1))).check(z.minLength(1)),
    });
  else if (pathname === '/payments/config')
    schema = z.looseObject({
      provider: z.literal('stripe'),
      mode: z.enum(['test', 'live', 'disabled', 'simulated']),
      publishableKey: z.nullable(text),
      web: z.looseObject({ enabled: z.boolean(), turnstileSiteKey: z.nullable(text) }),
      wallets: z.looseObject({ applePay: z.boolean(), googlePay: z.boolean() }),
    });
  else if (pathname === '/payments/checkout-policy')
    schema = z.looseObject({
      policyVersion: text.check(z.minLength(1)),
      maximumAmount: amount.check(z.gt(0)),
      minimumAmounts: z.record(currencySchema, amount),
    });
  else if (pathname === '/checkout-quotes') schema = quote;
  else if (/^\/checkouts\/[a-f0-9-]+$/i.test(pathname)) schema = checkoutSchema;
  else if (
    /^\/checkouts\/[a-f0-9-]+\/documents$/i.test(pathname) ||
    /^\/me\/donations\/[a-f0-9-]+\/giving-documents$/i.test(pathname)
  )
    schema = documentsSchema;
  else if (/^\/checkouts\/[a-f0-9-]+\/payment-intent$/i.test(pathname))
    schema = z
      .looseObject({
        checkoutId: id,
        status: z.enum(['ready', 'processing', 'completed', 'cancelled', 'review_required']),
        clientSecret: z.optional(text.check(z.minLength(1))),
        simulation: z.boolean(),
      })
      .check(
        z.refine(
          (intent) => intent.status !== 'ready' || intent.simulation || !!intent.clientSecret,
        ),
      );
  else if (pathname === '/checkouts/recover')
    schema = z.union([
      z.looseObject({ outcome: z.literal('RETIRED_UNCREATED') }),
      z.looseObject({ outcome: z.literal('RECOVERED'), checkout: checkoutSchema }),
    ]);
  else if (pathname === '/me/giving-events')
    schema = z.looseObject({ items: z.array(checkoutSchema), total: amount });
  if (!schema) return value;
  const result = schema.safeParse(value);
  if (!result.success)
    throw new DonorError(
      pathname === '/payments/config' ? 503 : 0,
      pathname === '/payments/config' ? 'WEB_CHECKOUT_DISABLED' : 'INVALID_API_RESPONSE',
    );
  return result.data;
}
